import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '../../ComponnentsUI/common/Input';
import { Select } from '../../ComponnentsUI/common/Select';
import type {
  SelectOption,
  BiometryField,
  MorphologyItem,
} from './lib/form-config';
import { OPTIONS } from './lib/form-config';
import {
  useReportData,
  type FetalAnatomyItem,
  type AnatomyStatus,
  type MeasurementSource,
} from './lib/report-context';
import { LuMessageSquare, LuEye } from 'react-icons/lu';
import { FrameEditorModal } from './frame-editor-modal';
import { FindingExplainEye } from './finding-explainability';

export const styles = {
  input:
    'h-7 text-xs px-1.5 border-slate-300 dark:border-gray-600 dark:bg-gray-800',
  select:
    'h-7 text-xs px-1 border-slate-300 dark:border-gray-600 dark:bg-gray-800',
  label:
    'text-[11px] font-medium text-slate-500 dark:text-gray-400 whitespace-nowrap',
  section:
    'text-xs font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wide border-b border-blue-200 dark:border-blue-800 pb-0.5 mb-1.5',
} as const;

interface MiniSelectProps {
  defaultValue?: string;
  value?: string;
  placeholder?: string;
  options: readonly SelectOption[];
  className?: string;
  disabled?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
}

export function MiniSelect({
  defaultValue,
  value,
  placeholder,
  options,
  className,
  disabled,
  onChange,
}: MiniSelectProps) {
  const { t } = useTranslation('Report');
  const translatedOptions = options.map((o) => ({
    ...o,
    label: t(o.label),
  }));

  return (
    <Select
      {...(value !== undefined
        ? { value }
        : defaultValue !== undefined
          ? { defaultValue }
          : {})}
      placeholder={placeholder ? t(placeholder) : t('ui.select')}
      options={translatedOptions}
      className={`${styles.select} ${className ?? ''}`}
      disabled={disabled}
      onChange={onChange}
    />
  );
}

const BIOMETRY_BACKEND_MAP: Record<string, string> = {
  bpd: 'bpd_cm',
  'bpd-crl84': 'bpd_cm',
  hc: 'hc_cm',
  ac: 'ac_cm',
  fl: 'fl_cm',
  crl: 'crl_cm',
  nt: 'nt_cm',
  tcd: 'tcd_cm',
  cerebellum: 'tcd_cm',
  hl: 'hl_cm',
  tibia: 'tibia_cm',
  fibula: 'fibula_cm',
  radius: 'radius_cm',
  ulna: 'ulna_cm',
  foot: 'foot_length_cm',
  iod: 'iod_cm',
  nasal: 'nasal_bone_t2_cm',
  'lat-vent': 'lateral_ventricle_cm',
  csp: 'csp_width_cm',
  cisterna: 'cisterna_magna_cm',
  '3rd-vent': 'third_ventricle_cm',
  vermis: 'vermis_height_cm',
  choroid: 'choroid_plexus_cm',
};

// Maps a form field key to the segmentation measurement key (MEAS__*) so the row
const FIELD_TO_MEAS_KEY: Record<string, string> = {
  bpd: 'MEAS__BPD',
  'bpd-crl84': 'MEAS__BPD',
  hc: 'MEAS__HC',
  ac: 'MEAS__AC',
  fl: 'MEAS__FL',
  crl: 'MEAS__CRL',
  nt: 'MEAS__NT',
  tcd: 'MEAS__TCD',
  cerebellum: 'MEAS__TCD',
  hl: 'MEAS__HL',
  tibia: 'MEAS__TIBIA_LENGTH',
  iod: 'MEAS__IOD',
  'lat-vent': 'MEAS__LATERAL_VENTRICLE_WIDTH',
  csp: 'MEAS__CSP_WIDTH',
  cisterna: 'MEAS__CISTERNA_MAGNA',
  choroid: 'MEAS__CHOROID_PLEXUS',
};

const PERCENTILE_BACKEND_MAP: Record<string, string> = {
  bpd: 'bpd_percentile',
  'bpd-crl84': 'bpd_percentile',
  hc: 'hc_percentile',
  ac: 'ac_percentile',
  fl: 'fl_percentile',
  tcd: 'tcd_percentile',
  cerebellum: 'tcd_percentile',
  hl: 'hl_percentile',
};

const BAR_W = 72;
const BAR_H = 14;
const CY = BAR_H / 2;
const OVERHANG = 10;

const TICKS = [3, 10, 50, 90, 97] as const;

function pctToX(pct: number): number {
  return (Math.max(0, Math.min(100, pct)) / 100) * BAR_W;
}

function zoneColor(pct: number): string {
  if (pct < 3 || pct > 97) {
    return '#ef4444'; // red – pathological
  }
  if (pct < 10 || pct > 90) {
    return '#f97316'; // orange – borderline
  }
  return '#475569'; // slate – normal
}

interface PercentileBarProps {
  percentile?: number;
}

export function PercentileBar({ percentile }: PercentileBarProps) {
  if (percentile === undefined || percentile === null) {
    return (
      <div className="flex items-center gap-1 w-28">
        <span className="text-[10px] text-slate-300 dark:text-gray-600">—</span>
      </div>
    );
  }

  const clamped = Math.max(0.5, Math.min(99.5, percentile));
  const mx = pctToX(clamped);
  const color = zoneColor(percentile);
  const isLow = percentile < 3;
  const isHigh = percentile > 97;
  const label =
    percentile < 1
      ? '<1%'
      : percentile > 99
        ? '>99%'
        : `${Math.round(percentile)}%`;

  return (
    <div className="flex items-center gap-1.5 shrink-0">
      <svg
        width={BAR_W + (isLow ? OVERHANG : 0) + (isHigh ? OVERHANG : 0)}
        height={BAR_H}
        viewBox={`${isLow ? -OVERHANG : 0} 0 ${BAR_W + (isLow ? OVERHANG : 0) + (isHigh ? OVERHANG : 0)} ${BAR_H}`}
        className="overflow-visible"
      >
        {isLow && (
          <line
            x1={-OVERHANG}
            y1={CY}
            x2={0}
            y2={CY}
            stroke={color}
            strokeWidth={1}
            strokeDasharray="2,2"
          />
        )}

        <line
          x1={0}
          y1={CY}
          x2={BAR_W}
          y2={CY}
          stroke="#cbd5e1"
          strokeWidth={1}
        />

        {TICKS.map((t) => (
          <line
            key={t}
            x1={pctToX(t)}
            y1={CY - 4}
            x2={pctToX(t)}
            y2={CY + 4}
            stroke="#94a3b8"
            strokeWidth={1}
          />
        ))}

        {isHigh && (
          <line
            x1={BAR_W}
            y1={CY}
            x2={BAR_W + OVERHANG}
            y2={CY}
            stroke={color}
            strokeWidth={1}
            strokeDasharray="2,2"
          />
        )}

        <polygon
          points={`${mx},${CY - 5} ${mx + 5},${CY} ${mx},${CY + 5} ${mx - 5},${CY}`}
          fill={color}
        />
      </svg>

      <span
        className="text-[10px] font-semibold w-8 text-right tabular-nums"
        style={{ color }}
      >
        {label}
      </span>
    </div>
  );
}

interface BiometryRowProps {
  label: string;
  unit?: string;
  placeholder?: string;
  showPercentile?: boolean;
  value?: number;
  fieldKey?: string;
  /** Frame the measurement was drawn on — enables the "see source image" eye. */
  source?: MeasurementSource;
  onExplain?: () => void;
  /** Keep the eye column aligned across rows when siblings have a source. */
  reserveExplainSlot?: boolean;
}

export function BiometryRow({
  label,
  unit = 'cm',
  placeholder,
  showPercentile = true,
  value,
  fieldKey,
  source,
  onExplain,
  reserveExplainSlot = false,
}: BiometryRowProps) {
  const { t } = useTranslation('Report');
  const { measurementValues, onUpdateFindings } = useReportData();
  const backendKey = fieldKey ? BIOMETRY_BACKEND_MAP[fieldKey] : undefined;
  const pctKey = fieldKey ? PERCENTILE_BACKEND_MAP[fieldKey] : undefined;

  const pctValue =
    pctKey && measurementValues[pctKey] != null
      ? Number(measurementValues[pctKey])
      : undefined;

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (!backendKey || !onUpdateFindings) {
      return;
    }
    const v = e.target.value ? parseFloat(e.target.value) : null;
    onUpdateFindings({ [backendKey]: v });
  };

  const translatedLabel = t(label);
  const explainTitle = t('ui.seeSourceImage');

  return (
    <div className="flex items-center gap-1">
      <span
        className={`${styles.label} w-24 shrink-0 truncate`}
        title={translatedLabel}
      >
        {translatedLabel}
      </span>
      <Input
        type="number"
        step="0.1"
        placeholder={placeholder}
        defaultValue={value !== undefined ? value : undefined}
        className={`${styles.input} w-20 text-center`}
        onBlur={backendKey ? handleBlur : undefined}
      />
      <span className="text-[10px] text-slate-400 dark:text-gray-500 w-6 shrink-0">
        {unit}
      </span>
      {(reserveExplainSlot || source) && (
        <span className="w-5 shrink-0 flex items-center justify-center">
          {source && onExplain && (
            <button
              type="button"
              onClick={onExplain}
              title={explainTitle}
              aria-label={`${explainTitle} — ${translatedLabel}`}
              className="p-0.5 rounded text-slate-400 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors"
            >
              <LuEye size={13} />
            </button>
          )}
        </span>
      )}
      {showPercentile && <PercentileBar percentile={pctValue} />}
    </div>
  );
}

export function BiometryHeader({
  hasExplainSlot = false,
}: {
  hasExplainSlot?: boolean;
}) {
  const { t } = useTranslation('Report');

  return (
    <div className="flex items-center gap-1 mb-0.5">
      <span className={`${styles.label} w-24`} />
      <span className="text-[10px] text-slate-400 dark:text-gray-500 w-20 text-center">
        {t('ui.value')}
      </span>
      <span className="text-[10px] text-slate-400 dark:text-gray-500 w-6" />
      {hasExplainSlot && <span className="w-5 shrink-0" />}
      <div className="flex items-center gap-1.5">
        <svg width={72} height={10} className="overflow-visible">
          <line x1={0} y1={5} x2={72} y2={5} stroke="#e2e8f0" strokeWidth={1} />
          {([3, 10, 50, 90, 97] as const).map((t) => (
            <text
              key={t}
              x={(t / 100) * 72}
              y={10}
              textAnchor="middle"
              fontSize={7}
              fill="#94a3b8"
            >
              {t}
            </text>
          ))}
        </svg>
        <span className="text-[10px] text-slate-400 dark:text-gray-500 w-8 text-right">
          %ile
        </span>
      </div>
    </div>
  );
}

export function BiometryList({
  fields,
  values,
}: {
  fields: readonly BiometryField[];
  values?: Record<string, number | undefined>;
}) {
  const { t } = useTranslation('Report');
  const reportData = useReportData();
  const resolvedValues = values ?? reportData.biometryValues;
  const { measurementSources, studyUID } = reportData;

  // Field key → frame the value was measured on (undefined for manual-only fields).
  const sourceByFieldKey = useMemo(() => {
    const map = new Map<string, MeasurementSource>();
    if (!measurementSources?.length) {
      return map;
    }
    for (const f of fields) {
      const measKey = FIELD_TO_MEAS_KEY[f.key];
      if (!measKey) {
        continue;
      }
      const src = measurementSources.find((s) => s.meas_key === measKey);
      if (src?.frame_id && src.sop_instance_uid) {
        map.set(f.key, src);
      }
    }
    return map;
  }, [fields, measurementSources]);

  // Fields with a source, in display order — the modal's prev/next walks this list.
  const explainableFields = useMemo(
    () => fields.filter((f) => sourceByFieldKey.has(f.key)),
    [fields, sourceByFieldKey]
  );

  const [explainIndex, setExplainIndex] = useState<number | null>(null);
  const openField =
    explainIndex != null ? (explainableFields[explainIndex] ?? null) : null;
  const openSource = openField
    ? (sourceByFieldKey.get(openField.key) ?? null)
    : null;

  const nav = useMemo(
    () =>
      explainIndex != null && explainableFields.length > 0
        ? {
            total: explainableFields.length,
            currentIndex: explainIndex,
            onNavigate: (index: number) => {
              if (index >= 0 && index < explainableFields.length) {
                setExplainIndex(index);
              }
            },
          }
        : undefined,
    [explainIndex, explainableFields.length]
  );

  const hasExplainSlot = sourceByFieldKey.size > 0;

  // Stable identity so the editor doesn't remount/re-render on unrelated form updates.
  const editorTarget = useMemo(() => {
    if (!openField || !openSource) {
      return null;
    }
    return {
      frame_id: openSource.frame_id,
      display_name: openSource.display_name
        ? `${t(openField.label)} — ${openSource.display_name}`
        : t(openField.label),
      view_code: openSource.view_code,
      view_def_id: openSource.view_def_id,
      sop_instance_uid: openSource.sop_instance_uid,
      serie_instance_uid: openSource.serie_instance_uid,
      format_file: openSource.format_file,
      frame_count: openSource.frame_count,
      modality: openSource.modality,
      sop_class_uid: openSource.sop_class_uid,
      fps: openSource.fps,
      width: openSource.width,
      height: openSource.height,
    };
  }, [openField, openSource, t]);

  return (
    <div className="space-y-0.5">
      {fields.some((f) => f.showPercentile) && (
        <BiometryHeader hasExplainSlot={hasExplainSlot} />
      )}
      {fields.map((f) => {
        const source = sourceByFieldKey.get(f.key);
        return (
          <BiometryRow
            key={f.key}
            label={f.label}
            unit={f.unit}
            placeholder={f.placeholder}
            showPercentile={f.showPercentile}
            value={resolvedValues[f.key]}
            fieldKey={f.key}
            source={source}
            reserveExplainSlot={hasExplainSlot}
            onExplain={
              source
                ? () =>
                    setExplainIndex(
                      explainableFields.findIndex((e) => e.key === f.key)
                    )
                : undefined
            }
          />
        );
      })}

      {/* Measurement explainability: opens the exact frame the measurement was
          drawn on in the single-frame segmentation editor. Prev/next cycles
          through this list's measurements that have a source frame. */}
      {editorTarget && studyUID && (
        <FrameEditorModal
          studyUID={studyUID}
          target={editorTarget}
          subtitle={t('ui.sourceImageSubtitle')}
          nav={nav}
          onClose={() => setExplainIndex(null)}
        />
      )}
    </div>
  );
}

export function MorphologyRow({ label }: { label: string }) {
  const { t } = useTranslation('Report');
  const [showComment, setShowComment] = useState(false);
  const [comment, setComment] = useState('');
  const [seen, setSeen] = useState<'present' | 'absent'>('absent');
  const [status, setStatus] = useState<'normal' | 'abnormal'>('normal');

  const translatedLabel = t(label);

  return (
    <div className="border-b border-slate-100 dark:border-gray-700/50 last:border-b-0">
      <div className="flex items-center gap-1 py-1 px-1">
        <span
          className={`${styles.label} flex-1 truncate`}
          title={translatedLabel}
        >
          {translatedLabel}
        </span>
        <MiniSelect
          value={seen}
          options={OPTIONS.presentAbsent}
          className="w-20"
          onChange={(e) => setSeen(e.target.value as 'present' | 'absent')}
        />
        {seen === 'present' && (
          <MiniSelect
            value={status}
            options={OPTIONS.morphologyStatus}
            className={`w-24 ${status === 'abnormal' ? 'text-red-600 dark:text-red-400 font-semibold' : ''}`}
            onChange={(e) => setStatus(e.target.value as 'normal' | 'abnormal')}
          />
        )}
        <button
          type="button"
          onClick={() => setShowComment(!showComment)}
          title={showComment ? t('ui.hideComment') : t('ui.addCommentBtn')}
          className={`shrink-0 p-0.5 rounded transition-colors ${
            comment
              ? 'text-blue-500 dark:text-blue-400'
              : 'text-slate-300 dark:text-gray-600 hover:text-slate-500 dark:hover:text-gray-400'
          }`}
        >
          <LuMessageSquare size={14} />
        </button>
      </div>

      {showComment && (
        <div className="px-1 pb-1.5">
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={t('ui.addComment')}
            rows={2}
            className="w-full text-xs px-2 py-1.5 rounded-md border border-slate-200 dark:border-gray-600 bg-slate-50 dark:bg-gray-800 text-slate-700 dark:text-gray-200 placeholder-slate-400 dark:placeholder-gray-500 outline-none focus:ring-1 focus:ring-blue-400 resize-none"
          />
        </div>
      )}
    </div>
  );
}

export function MorphologyList({
  items,
}: {
  items: readonly MorphologyItem[];
}) {
  return (
    <div>
      {items.map((item) => (
        <MorphologyRow key={item.key} label={item.label} />
      ))}
    </div>
  );
}

/**
 * Standalone "see source image" eye for a single measurement input.
 * Looks the measurement up in the report's `measurement_sources`; renders
 * nothing when the value has no source frame (manual-only entries).
 */
export function MeasurementSourceEye({
  measKey,
  labelKey,
}: {
  /** Segmentation measurement key, e.g. "MEAS__SDP". */
  measKey: string;
  /** i18n key (or literal) used as the modal title. */
  labelKey: string;
}) {
  const { t } = useTranslation('Report');
  const { measurementSources, studyUID } = useReportData();
  const [open, setOpen] = useState(false);

  const source = useMemo(
    () =>
      measurementSources?.find(
        (s) => s.meas_key === measKey && s.frame_id && s.sop_instance_uid
      ) ?? null,
    [measurementSources, measKey]
  );

  const editorTarget = useMemo(() => {
    if (!source) {
      return null;
    }
    const label = t(labelKey);
    return {
      frame_id: source.frame_id,
      display_name: source.display_name
        ? `${label} — ${source.display_name}`
        : label,
      view_code: source.view_code,
      view_def_id: source.view_def_id,
      sop_instance_uid: source.sop_instance_uid,
      serie_instance_uid: source.serie_instance_uid,
      format_file: source.format_file,
      frame_count: source.frame_count,
      modality: source.modality,
      sop_class_uid: source.sop_class_uid,
      fps: source.fps,
      width: source.width,
      height: source.height,
    };
  }, [source, labelKey, t]);

  if (!editorTarget || !studyUID) {
    return null;
  }

  const explainTitle = t('ui.seeSourceImage');

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title={explainTitle}
        aria-label={`${explainTitle} — ${t(labelKey)}`}
        className="shrink-0 p-0.5 rounded text-slate-400 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors align-middle"
      >
        <LuEye size={12} />
      </button>
      {open && (
        <FrameEditorModal
          studyUID={studyUID}
          target={editorTarget}
          subtitle={t('ui.sourceImageSubtitle')}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

interface FieldProps {
  label: string;
  /** When set, shows the "see source image" eye next to the label if the
      measurement has a source frame in this report. */
  explainMeasKey?: string;
  explainFindingKey?: string;
  children: React.ReactNode;
}

export function Field({
  label,
  explainMeasKey,
  explainFindingKey,
  children,
}: FieldProps) {
  const { t } = useTranslation('Report');
  return (
    <div>
      <span className="inline-flex items-center gap-0.5 max-w-full">
        <span className={styles.label}>{t(label)}</span>
        {explainMeasKey && (
          <MeasurementSourceEye measKey={explainMeasKey} labelKey={label} />
        )}
        {explainFindingKey && (
          <FindingExplainEye findingKey={explainFindingKey} labelKey={label} />
        )}
      </span>
      {children}
    </div>
  );
}

export function SectionHeading({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation('Report');
  const content = typeof children === 'string' ? t(children) : children;
  return <div className={styles.section}>{content}</div>;
}

interface NumberInputProps {
  placeholder?: string;
  step?: number;
  defaultValue?: number;
  min?: number;
  max?: number;
  className?: string;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
}

export function NumberInput({
  placeholder,
  step = 0.1,
  defaultValue,
  min,
  max,
  className,
  onBlur,
}: NumberInputProps) {
  return (
    <Input
      type="number"
      step={step}
      placeholder={placeholder}
      defaultValue={defaultValue}
      min={min}
      max={max}
      className={`${styles.input} text-center ${className ?? ''}`}
      onBlur={onBlur}
    />
  );
}

interface InlineSelectProps {
  label: string;
  defaultValue?: string;
  placeholder?: string;
  options: readonly SelectOption[];
  selectClassName?: string;
  onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
}

export function InlineSelect({
  label,
  defaultValue,
  placeholder,
  options,
  selectClassName = 'flex-1',
  onChange,
}: InlineSelectProps) {
  const { t } = useTranslation('Report');
  return (
    <div className="flex items-center gap-1">
      <span className={`${styles.label} w-24`}>{t(label)}</span>
      <MiniSelect
        defaultValue={defaultValue}
        placeholder={placeholder}
        options={options}
        className={selectClassName}
        onChange={onChange}
      />
    </div>
  );
}

interface InlineNumberProps {
  label: string;
  placeholder?: string;
  step?: number;
  defaultValue?: number;
  inputClassName?: string;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
}

export function InlineNumber({
  label,
  placeholder,
  step = 0.01,
  defaultValue,
  inputClassName = 'w-16',
  onBlur,
}: InlineNumberProps) {
  const { t } = useTranslation('Report');
  return (
    <div className="flex items-center gap-1">
      <span className={`${styles.label} w-24`}>{t(label)}</span>
      <NumberInput
        placeholder={placeholder}
        step={step}
        defaultValue={defaultValue}
        className={inputClassName}
        onBlur={onBlur}
      />
    </div>
  );
}

interface TwoColumnLayoutProps {
  left: React.ReactNode;
  right: React.ReactNode;
}

export function TwoColumnLayout({ left, right }: TwoColumnLayoutProps) {
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] items-stretch gap-x-3 gap-y-2">
      <div className="h-full min-w-0 space-y-2">{left}</div>
      <div className="h-full min-w-0 space-y-2">{right}</div>
    </div>
  );
}

export function ScreeningSection() {
  const { screening, onUpdateFindings } = useReportData();

  const boolToScreening = (val?: boolean | null): string => {
    if (val === true) {
      return 'positive';
    }
    if (val === false) {
      return 'negative';
    }
    return 'not-done';
  };

  const screeningToBool = (val: string): boolean | null => {
    if (val === 'positive') {
      return true;
    }
    if (val === 'negative') {
      return false;
    }
    return null;
  };

  return (
    <>
      <SectionHeading>sections.screening</SectionHeading>
      <div className="grid grid-cols-2 gap-1">
        <Field
          label="fields.preeclampsiaScreening"
          explainFindingKey="pre_eclampsia"
        >
          <MiniSelect
            defaultValue={boolToScreening(screening.preEclampsia)}
            options={OPTIONS.positiveNegative}
            onChange={(e) =>
              onUpdateFindings?.({
                pre_eclampsia: screeningToBool(e.target.value),
              })
            }
          />
        </Field>
        <Field
          label="fields.gestationalDiabetes"
          explainFindingKey="gestational_diabetes"
        >
          <MiniSelect
            defaultValue={boolToScreening(screening.gestationalDiabetes)}
            options={OPTIONS.positiveNegative}
            onChange={(e) =>
              onUpdateFindings?.({
                gestational_diabetes: screeningToBool(e.target.value),
              })
            }
          />
        </Field>
        <Field
          label="fields.multiplePregnancy"
          explainFindingKey="pregnancy_type"
        >
          <MiniSelect
            defaultValue={boolToScreening(screening.isTwins)}
            options={OPTIONS.positiveNegative}
            onChange={(e) =>
              onUpdateFindings?.({
                is_twins: screeningToBool(e.target.value),
              })
            }
          />
        </Field>
      </div>
    </>
  );
}

const STATUS_DOT: Record<AnatomyStatus, string> = {
  visualized: 'bg-emerald-500',
  not_visualized: 'bg-slate-300 dark:bg-gray-600',
};

const REGION_ORDER = [
  'Brain & CNS',
  'Cardiovascular',
  'Thorax & Abdomen',
  'Skeletal & Limbs',
  'Face & Head',
  'Placenta & Fluid',
  'Maternal Structures',
  'Biometry Planes',
];

const FETAL_ANATOMY_MASTER: ReadonlyArray<
  Pick<FetalAnatomyItem, 'category_code' | 'structure_name' | 'region'>
> = [
  {
    category_code: 'cavum_septum_pellucidum',
    structure_name: 'Cavum Septum Pellucidum',
    region: 'Brain & CNS',
  },
  {
    category_code: 'cerebellum',
    structure_name: 'Cerebellum',
    region: 'Brain & CNS',
  },
  {
    category_code: 'cerebral_peduncle',
    structure_name: 'Cerebral Peduncle',
    region: 'Brain & CNS',
  },
  {
    category_code: 'choroid_plexus',
    structure_name: 'Choroid Plexus',
    region: 'Brain & CNS',
  },
  {
    category_code: 'cisterna_magna',
    structure_name: 'Cisterna Magna',
    region: 'Brain & CNS',
  },
  {
    category_code: 'hippocampal_gyri',
    structure_name: 'Hippocampal Gyri',
    region: 'Brain & CNS',
  },
  {
    category_code: 'sylvian_fissure',
    structure_name: 'Sylvian Fissure',
    region: 'Brain & CNS',
  },
  {
    category_code: 'thalamus',
    structure_name: 'Thalamus',
    region: 'Brain & CNS',
  },
  { category_code: 'vermis', structure_name: 'Vermis', region: 'Brain & CNS' },
  { category_code: 'heart', structure_name: 'Heart', region: 'Cardiovascular' },
  {
    category_code: 'left_atrium',
    structure_name: 'Left Atrium',
    region: 'Cardiovascular',
  },
  {
    category_code: 'right_atrium',
    structure_name: 'Right Atrium',
    region: 'Cardiovascular',
  },
  {
    category_code: 'left_ventricle',
    structure_name: 'Left Ventricle',
    region: 'Cardiovascular',
  },
  {
    category_code: 'right_ventricle',
    structure_name: 'Right Ventricle',
    region: 'Cardiovascular',
  },
  {
    category_code: 'inter_atrial_septum',
    structure_name: 'Inter-atrial Septum',
    region: 'Cardiovascular',
  },
  {
    category_code: 'interventricular_septum',
    structure_name: 'Interventricular Septum',
    region: 'Cardiovascular',
  },
  {
    category_code: 'mitral_valve',
    structure_name: 'Mitral Valve',
    region: 'Cardiovascular',
  },
  {
    category_code: 'tricuspid_valve',
    structure_name: 'Tricuspid Valve',
    region: 'Cardiovascular',
  },
  {
    category_code: 'pulmonary_artery',
    structure_name: 'Pulmonary Artery',
    region: 'Cardiovascular',
  },
  { category_code: 'aorta', structure_name: 'Aorta', region: 'Cardiovascular' },
  {
    category_code: 'foramen_ovale',
    structure_name: 'Foramen Ovale',
    region: 'Cardiovascular',
  },
  {
    category_code: 'lungs',
    structure_name: 'Lungs',
    region: 'Thorax & Abdomen',
  },
  {
    category_code: 'stomach_bubble',
    structure_name: 'Stomach Bubble',
    region: 'Thorax & Abdomen',
  },
  {
    category_code: 'fetal_kidneys',
    structure_name: 'Fetal Kidneys',
    region: 'Thorax & Abdomen',
  },
  {
    category_code: 'fetal_bladder',
    structure_name: 'Fetal Bladder',
    region: 'Thorax & Abdomen',
  },
  {
    category_code: 'umbilical_vein',
    structure_name: 'Fetal Liver / Umbilical Vein',
    region: 'Thorax & Abdomen',
  },
  {
    category_code: 'portal_sinus',
    structure_name: 'Portal Sinus',
    region: 'Thorax & Abdomen',
  },
  {
    category_code: 'umbilical_cord',
    structure_name: 'Umbilical Cord',
    region: 'Thorax & Abdomen',
  },
  {
    category_code: 'inferior_vena_cava',
    structure_name: 'Inferior Vena Cava',
    region: 'Thorax & Abdomen',
  },
  {
    category_code: 'superior_vena_cava',
    structure_name: 'Superior Vena Cava',
    region: 'Thorax & Abdomen',
  },
  { category_code: 'ribs', structure_name: 'Ribs', region: 'Thorax & Abdomen' },
  {
    category_code: 'fetal_spine',
    structure_name: 'Fetal Spine',
    region: 'Thorax & Abdomen',
  },
  {
    category_code: 'genitalia',
    structure_name: 'Genitalia',
    region: 'Thorax & Abdomen',
  },
  {
    category_code: 'femur',
    structure_name: 'Femur',
    region: 'Skeletal & Limbs',
  },
  {
    category_code: 'arm_humerus',
    structure_name: 'Humerus',
    region: 'Skeletal & Limbs',
  },
  {
    category_code: 'forearm_bones',
    structure_name: 'Forearm Bones',
    region: 'Skeletal & Limbs',
  },
  {
    category_code: 'leg_bones',
    structure_name: 'Leg Bones',
    region: 'Skeletal & Limbs',
  },
  { category_code: 'foot', structure_name: 'Foot', region: 'Skeletal & Limbs' },
  { category_code: 'hand', structure_name: 'Hand', region: 'Skeletal & Limbs' },
  {
    category_code: 'femur_extremity',
    structure_name: 'Fetal Segmentation',
    region: 'Skeletal & Limbs',
  },
  { category_code: 'orbit', structure_name: 'Orbit', region: 'Face & Head' },
  {
    category_code: 'nasal_bone',
    structure_name: 'Nasal Bone',
    region: 'Face & Head',
  },
  {
    category_code: 'hard_palate',
    structure_name: 'Hard Palate',
    region: 'Face & Head',
  },
  {
    category_code: 'mandible',
    structure_name: 'Mandible',
    region: 'Face & Head',
  },
  {
    category_code: 'nuchal_translucency',
    structure_name: 'Nuchal Translucency',
    region: 'Face & Head',
  },
  {
    category_code: 'fetal_brain',
    structure_name: 'Cephalic Plane',
    region: 'Face & Head',
  },
  {
    category_code: 'placenta',
    structure_name: 'Placenta',
    region: 'Placenta & Fluid',
  },
  {
    category_code: 'amniotic_fluid',
    structure_name: 'Amniotic Fluid Pocket',
    region: 'Placenta & Fluid',
  },
  {
    category_code: 'cervix',
    structure_name: 'Cervix',
    region: 'Placenta & Fluid',
  },
  {
    category_code: 'myometrium',
    structure_name: 'Myometrium',
    region: 'Placenta & Fluid',
  },
  {
    category_code: 'maternal_bladder',
    structure_name: 'Maternal Bladder',
    region: 'Maternal Structures',
  },
  {
    category_code: 'abdomen',
    structure_name: 'AC Plane',
    region: 'Biometry Planes',
  },
  {
    category_code: 'cephalic_pole_for_crl',
    structure_name: 'CRL',
    region: 'Biometry Planes',
  },
];

function createDefaultAnatomyItem(
  item: Pick<FetalAnatomyItem, 'category_code' | 'structure_name' | 'region'>
): FetalAnatomyItem {
  return {
    category_id: null,
    category_code: item.category_code,
    structure_name: item.structure_name,
    region: item.region,
    status: 'not_visualized',
    source: null,
  };
}

function sortAnatomyItems(items: FetalAnatomyItem[]) {
  return [...items].sort((a, b) => {
    const regionA = REGION_ORDER.indexOf(a.region);
    const regionB = REGION_ORDER.indexOf(b.region);
    const orderA = regionA === -1 ? REGION_ORDER.length : regionA;
    const orderB = regionB === -1 ? REGION_ORDER.length : regionB;

    if (orderA !== orderB) {
      return orderA - orderB;
    }

    return a.structure_name.localeCompare(b.structure_name);
  });
}

function mergeFetalAnatomyWithDefaults(
  anatomy: FetalAnatomyItem[]
): FetalAnatomyItem[] {
  const byCode = new Map<string, FetalAnatomyItem>();

  for (const item of FETAL_ANATOMY_MASTER) {
    byCode.set(item.category_code, createDefaultAnatomyItem(item));
  }

  for (const item of anatomy) {
    byCode.set(item.category_code, {
      ...byCode.get(item.category_code),
      ...item,
    });
  }

  return sortAnatomyItems(Array.from(byCode.values()));
}

type AnatomyDisplayLocale = 'en' | 'fr';

const ANATOMY_REGION_DISPLAY: Record<
  string,
  Record<AnatomyDisplayLocale, string>
> = {
  'Brain & CNS': {
    en: 'Brain and central nervous system',
    fr: 'Cerveau et système nerveux central',
  },
  Cardiovascular: {
    en: 'Heart and great vessels',
    fr: 'Cœur et gros vaisseaux',
  },
  'Thorax & Abdomen': {
    en: 'Thorax, abdomen and pelvis',
    fr: 'Thorax, abdomen et pelvis',
  },
  'Skeletal & Limbs': {
    en: 'Limbs and skeleton',
    fr: 'Membres et squelette',
  },
  'Face & Head': {
    en: 'Face and neck',
    fr: 'Face et cou',
  },
  'Placenta & Fluid': {
    en: 'Placenta, cervix and amniotic fluid',
    fr: 'Placenta, col et liquide amniotique',
  },
  'Maternal Structures': {
    en: 'Maternal pelvic structures',
    fr: 'Structures pelviennes maternelles',
  },
  'Biometry Planes': {
    en: 'Biometry reference planes',
    fr: 'Plans de référence biométriques',
  },
};

const ANATOMY_STRUCTURE_DISPLAY: Record<
  string,
  Record<AnatomyDisplayLocale, string>
> = {
  cavum_septum_pellucidum: {
    en: 'Cavum septi pellucidi',
    fr: 'Cavum du septum pellucidum',
  },
  cerebellum: { en: 'Cerebellum', fr: 'Cervelet' },
  cerebral_peduncle: {
    en: 'Midbrain / cerebral peduncles',
    fr: 'Mésencéphale / pédoncules cérébraux',
  },
  choroid_plexus: { en: 'Choroid plexuses', fr: 'Plexus choroïdes' },
  cisterna_magna: { en: 'Cisterna magna', fr: 'Grande citerne' },
  hippocampal_gyri: { en: 'Cerebral gyri', fr: 'Circonvolutions cérébrales' },
  sylvian_fissure: { en: 'Sylvian fissures', fr: 'Scissures de Sylvius' },
  thalamus: { en: 'Thalami', fr: 'Thalamus' },
  vermis: { en: 'Cerebellar vermis', fr: 'Vermis cérébelleux' },
  heart: { en: 'Heart', fr: 'Cœur' },
  left_atrium: { en: 'Left atrium', fr: 'Oreillette gauche' },
  right_atrium: { en: 'Right atrium', fr: 'Oreillette droite' },
  left_ventricle: { en: 'Left ventricle', fr: 'Ventricule gauche' },
  right_ventricle: { en: 'Right ventricle', fr: 'Ventricule droit' },
  inter_atrial_septum: {
    en: 'Interatrial septum',
    fr: 'Septum interauriculaire',
  },
  interventricular_septum: {
    en: 'Interventricular septum',
    fr: 'Septum interventriculaire',
  },
  mitral_valve: { en: 'Mitral valve', fr: 'Valve mitrale' },
  tricuspid_valve: { en: 'Tricuspid valve', fr: 'Valve tricuspide' },
  pulmonary_artery: { en: 'Pulmonary artery', fr: 'Artère pulmonaire' },
  aorta: { en: 'Aorta', fr: 'Aorte' },
  foramen_ovale: { en: 'Foramen ovale', fr: 'Foramen ovale' },
  lungs: { en: 'Lungs', fr: 'Poumons' },
  stomach_bubble: { en: 'Stomach bubble', fr: 'Estomac' },
  fetal_kidneys: { en: 'Kidneys', fr: 'Reins' },
  fetal_bladder: { en: 'Bladder', fr: 'Vessie' },
  umbilical_vein: {
    en: 'Liver and umbilical vein',
    fr: 'Foie et veine ombilicale',
  },
  portal_sinus: { en: 'Portal sinus', fr: 'Sinus portal' },
  umbilical_cord: { en: 'Umbilical cord', fr: 'Cordon ombilical' },
  inferior_vena_cava: {
    en: 'Inferior vena cava',
    fr: 'Veine cave inférieure',
  },
  superior_vena_cava: {
    en: 'Superior vena cava',
    fr: 'Veine cave supérieure',
  },
  ribs: { en: 'Ribs', fr: 'Côtes' },
  fetal_spine: { en: 'Spine', fr: 'Rachis' },
  genitalia: { en: 'External genitalia', fr: 'Organes génitaux externes' },
  femur: { en: 'Femur', fr: 'Fémur' },
  arm_humerus: { en: 'Humerus', fr: 'Humérus' },
  forearm_bones: { en: 'Forearm bones', fr: "Os de l'avant-bras" },
  leg_bones: { en: 'Leg bones', fr: 'Os de la jambe' },
  foot: { en: 'Feet', fr: 'Pieds' },
  hand: { en: 'Hands', fr: 'Mains' },
  femur_extremity: { en: 'Fetal extremities', fr: 'Extrémités fœtales' },
  orbit: { en: 'Orbits', fr: 'Orbites' },
  nasal_bone: { en: 'Nasal bone', fr: 'Os nasal' },
  hard_palate: { en: 'Palate', fr: 'Palais' },
  mandible: { en: 'Mandible', fr: 'Mandibule' },
  nuchal_translucency: { en: 'Nuchal translucency', fr: 'Clarté nucale' },
  fetal_brain: { en: 'Cephalic plane', fr: 'Plan céphalique' },
  placenta: { en: 'Placenta', fr: 'Placenta' },
  amniotic_fluid: { en: 'Amniotic fluid', fr: 'Liquide amniotique' },
  cervix: { en: 'Cervix', fr: 'Col utérin' },
  myometrium: { en: 'Myometrium', fr: 'Myomètre' },
  maternal_bladder: { en: 'Maternal bladder', fr: 'Vessie maternelle' },
  abdomen: {
    en: 'Abdominal circumference plane',
    fr: 'Plan du périmètre abdominal',
  },
  cephalic_pole_for_crl: {
    en: 'Crown-rump length plane',
    fr: 'Plan de la longueur cranio-caudale',
  },
};

function anatomyLocale(language?: string): AnatomyDisplayLocale {
  return language?.toLowerCase().startsWith('fr') ? 'fr' : 'en';
}

function anatomyRegionLabel(region: string, locale: AnatomyDisplayLocale) {
  return ANATOMY_REGION_DISPLAY[region]?.[locale] ?? region;
}

function anatomyStructureLabel(
  item: FetalAnatomyItem,
  locale: AnatomyDisplayLocale
) {
  return (
    ANATOMY_STRUCTURE_DISPLAY[item.category_code]?.[locale] ??
    item.structure_name
  );
}

export function FetalAnatomySection() {
  const { t, i18n } = useTranslation('Report');
  const { fetalAnatomy, onAnatomyStatusOverride, isLocked } = useReportData();
  const displayLocale = anatomyLocale(i18n.resolvedLanguage || i18n.language);
  const anatomyItems = useMemo(
    () => mergeFetalAnatomyWithDefaults(fetalAnatomy),
    [fetalAnatomy]
  );

  const grouped = useMemo(() => {
    const byRegion: Record<string, FetalAnatomyItem[]> = {};
    for (const item of anatomyItems) {
      if (!byRegion[item.region]) {
        byRegion[item.region] = [];
      }
      byRegion[item.region].push(item);
    }
    return byRegion;
  }, [anatomyItems]);

  const counts = useMemo(() => {
    let visualized = 0,
      notVis = 0;
    for (const item of anatomyItems) {
      if (item.status === 'visualized') {
        visualized++;
      } else {
        notVis++;
      }
    }
    return { visualized, notVis, total: anatomyItems.length };
  }, [anatomyItems]);

  const toggleStatus = (item: FetalAnatomyItem) => {
    if (isLocked || !onAnatomyStatusOverride) {
      return;
    }
    const next: AnatomyStatus =
      item.status === 'visualized' ? 'not_visualized' : 'visualized';
    onAnatomyStatusOverride(item.category_code, next, item);
  };

  return (
    <>
      <SectionHeading>sections.fetalAnatomy</SectionHeading>

      <div className="flex gap-3 text-[10px] font-medium px-1 pb-1">
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-emerald-500" />
          {counts.visualized} {t('anatomy.structuresVisualized')}
        </span>
        <span className="flex items-center gap-1 text-slate-400 dark:text-gray-500">
          <span className="w-2 h-2 rounded-full bg-slate-300 dark:bg-gray-600" />
          {counts.notVis} {t('anatomy.notFullyVisualized')}
        </span>
      </div>
      <div className="rounded-md border border-slate-200 bg-slate-50 px-2.5 py-2 text-[11px] leading-4 text-slate-600 dark:border-gray-700 dark:bg-gray-800/50 dark:text-gray-300">
        {counts.notVis === 0
          ? t('anatomy.completeReviewNote')
          : t('anatomy.partialReviewNote')}
      </div>

      <div className="space-y-1.5">
        {REGION_ORDER.map((region) => {
          const items = grouped[region];
          if (!items || items.length === 0) {
            return null;
          }
          const visualizedItems = items.filter(
            (i) => i.status === 'visualized'
          );
          const notVisualizedItems = items.filter(
            (i) => i.status !== 'visualized'
          );
          const renderItem = (item: FetalAnatomyItem) => {
            const displayName = anatomyStructureLabel(item, displayLocale);
            return (
              <button
                key={item.category_code}
                type="button"
                disabled={isLocked}
                onClick={() => toggleStatus(item)}
                className={`inline-flex max-w-full items-center gap-1 rounded-full border px-2 py-0.5 text-left text-[10px] leading-tight transition-colors
                  ${
                    item.status === 'visualized'
                      ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-300'
                      : 'border-slate-200 bg-white text-slate-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400'
                  }
                  ${isLocked ? 'cursor-default' : 'cursor-pointer hover:opacity-80'}
                `}
                title={`${displayName}: ${
                  item.status === 'visualized'
                    ? t('anatomy.visualized')
                    : t('anatomy.notFullyVisualized')
                }`}
              >
                <span
                  className={`h-1.5 w-1.5 shrink-0 rounded-full ${STATUS_DOT[item.status]}`}
                />
                <span className="min-w-0 whitespace-normal break-words">
                  {displayName}
                </span>
                {item.source === 'ai' && (
                  <span
                    className="shrink-0 text-[8px] text-blue-400 dark:text-blue-500"
                    title={t('anatomy.aiDetected')}
                  >
                    AI
                  </span>
                )}
                {item.source === 'manual' && (
                  <span
                    className="shrink-0 text-[8px] text-orange-400 dark:text-orange-500"
                    title={t('anatomy.manualEntry')}
                  >
                    M
                  </span>
                )}
              </button>
            );
          };
          return (
            <div
              key={region}
              className="rounded-lg border border-slate-200 dark:border-gray-700 overflow-hidden"
            >
              <div className="px-3 py-1 text-[11px] font-semibold bg-slate-50 dark:bg-gray-800/50 text-slate-700 dark:text-gray-300 flex justify-between">
                <span>{anatomyRegionLabel(region, displayLocale)}</span>
                <span className="text-slate-400 dark:text-gray-500 font-normal">
                  {visualizedItems.length}/{items.length}
                </span>
              </div>
              <div className="space-y-1.5 px-2 py-1.5">
                <div>
                  <div className="mb-1 text-[10px] font-semibold text-slate-500 dark:text-gray-400">
                    {t('anatomy.visualizedNoAbnormality')}
                  </div>
                  {visualizedItems.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {visualizedItems.map(renderItem)}
                    </div>
                  ) : (
                    <div className="text-[10px] text-slate-400 dark:text-gray-500">
                      {t('anatomy.noVisualizedInRegion')}
                    </div>
                  )}
                </div>
                {notVisualizedItems.length > 0 && (
                  <div>
                    <div className="mb-1 text-[10px] font-semibold text-amber-600 dark:text-amber-400">
                      {t('anatomy.notFullyVisualized')}
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {notVisualizedItems.map(renderItem)}
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}
